import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Link,
  Grid,
  Chip,
  Divider,
  Fade,
  useMediaQuery,
  Skeleton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { colors } from '../../styled/StyledComponents';
import { styled, keyframes } from '@mui/material/styles';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const ProfileImage = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 400,
  objectFit: 'cover',
  borderRadius: 16,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.5s ease',
  [theme.breakpoints.down('md')]: {
    height: 300,
  },
  [theme.breakpoints.down('sm')]: {
    height: 250,
  },
}));

const SocialLink = styled(Link)(({ theme, colorindex = 0 }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  textDecoration: 'none',
  color: [colors.primary, colors.secondary, colors.tertiary][colorindex % 3],
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  transition: 'all 0.3s ease',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: [
      `${colors.primary}10`, 
      `${colors.secondary}10`, 
      `${colors.tertiary}10`
    ][colorindex % 3],
    transform: 'translateY(-3px)',
  },
}));

const InfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 8,
  backgroundColor: 'rgba(0, 0, 0, 0.02)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    transform: 'translateX(5px)',
  },
}));

const SkillChip = styled(Chip)(({ theme, colorindex = 0 }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: [
    `${colors.primary}15`, 
    `${colors.secondary}15`, 
    `${colors.tertiary}15`
  ][colorindex % 3],
  color: [colors.primary, colors.secondary, colors.tertiary][colorindex % 3],
  fontWeight: 500,
  '&:hover': {
    backgroundColor: [
      `${colors.primary}25`, 
      `${colors.secondary}25`, 
      `${colors.tertiary}25`
    ][colorindex % 3],
  },
}));

const TeamMemberDialog = ({ member, open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isLoading, setIsLoading] = useState(true);
  const [colorIndex, setColorIndex] = useState(0);

  useEffect(() => {
    if (open) {
      // Generate a random color index for this member
      setColorIndex(Math.floor(Math.random() * 3));
      
      // Simulate loading content
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(true);
    }
  }, [open]);

  if (!member) return null;

  // Sample data - in a real app, this would come from the member object
  // For now, we make the sample data translatable.
  // Ideally, this data would come from the member object itself and be translated there.
  const skills = [
    t('teamDialog.sampleSkills.sustainability', 'Sustainability'), 
    t('teamDialog.sampleSkills.carbonCredits', 'Carbon Credits'), 
    t('teamDialog.sampleSkills.climateTech', 'Climate Tech'), 
    t('teamDialog.sampleSkills.businessStrategy', 'Business Strategy'), 
    t('teamDialog.sampleSkills.ai', 'AI')
  ];
  const education = t('teamDialog.sampleEducation', 'Degrees in Physics and Global Studies');
  const location = t('teamDialog.sampleLocation', 'Tokyo, Japan');
  const experience = t('teamDialog.sampleExperience', '15+ years in business strategy, data science, and sustainability at BCG, Kearney, and Rakuten');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          bgcolor: 'background.paper',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
        },
      }}
      TransitionComponent={Fade}
      TransitionProps={{
        timeout: 500,
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            zIndex: 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: 'white',
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.3s ease',
          }}
          aria-label="Close dialog"
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: { xs: 2, sm: 4 }, maxHeight: '90vh', overflow: 'auto' }}>
          <Grid container spacing={4}>
            {/* Left column - Image */}
            <Grid item xs={12} md={5}>
              {isLoading ? (
                <Skeleton 
                  variant="rectangular" 
                  width="100%" 
                  height={400} 
                  sx={{ borderRadius: 3 }} 
                />
              ) : (
                <ProfileImage
                  component="img"
                  src={member.image || "/images/team/daniel2.png"}
                  alt={member.name}
                  sx={{
                    animation: `${fadeIn} 0.5s ease forwards`,
                  }}
                />
              )}

              {!isLoading && (
                <Box 
                  sx={{ 
                    mt: 3,
                    animation: `${fadeIn} 0.5s ease forwards 0.2s`,
                    opacity: 0,
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t('teamDialog.connect', 'Connect')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {member.linkedin && (
                      <SocialLink
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        colorindex={colorIndex}
                      >
                        <LinkedInIcon />
                        <Typography>LinkedIn</Typography>
                      </SocialLink>
                    )}
                    {member.twitter && (
                      <SocialLink
                        href={member.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        colorindex={colorIndex + 1}
                      >
                        <TwitterIcon />
                        <Typography>Twitter</Typography>
                      </SocialLink>
                    )}
                    <SocialLink
                      href={`mailto:${member.email || 'contact@sustefy.com'}`}
                      colorindex={colorIndex + 2}
                    >
                      <EmailIcon />
                      <Typography>Email</Typography>
                    </SocialLink>
                  </Box>
                </Box>
              )}
            </Grid>

            {/* Right column - Info */}
            <Grid item xs={12} md={7}>
              {isLoading ? (
                <>
                  <Skeleton variant="text" width="70%" height={60} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="50%" height={30} sx={{ mb: 3 }} />
                  
                  <Skeleton variant="text" width="100%" height={25} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" height={25} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="90%" height={25} sx={{ mb: 3 }} />
                  
                  <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} variant="rectangular" width={80} height={32} sx={{ borderRadius: 4 }} />
                    ))}
                  </Box>
                  
                  <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" width="100%" height={50} sx={{ mb: 2, borderRadius: 1 }} />
                  ))}
                </>
              ) : (
                <>
                  <Box 
                    sx={{ 
                      animation: `${fadeIn} 0.5s ease forwards`,
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: colors.text,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                      }}
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: [colors.primary, colors.secondary, colors.tertiary][colorIndex % 3],
                        fontWeight: 600,
                        mb: 3,
                      }}
                    >
                      {member.role}
                    </Typography>
                  </Box>

                  <Box 
                    sx={{ 
                      animation: `${fadeIn} 0.5s ease forwards 0.1s`,
                      opacity: 0,
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: colors.textLight,
                        mb: 3,
                        lineHeight: 1.8,
                      }}
                    >
                      {member.bio || t('landing.about.team.members.0.bio')}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box 
                    sx={{ 
                      animation: `${fadeIn} 0.5s ease forwards 0.2s`,
                      opacity: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {t('teamDialog.expertise', 'Expertise')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 3 }}>
                      {skills.map((skill, index) => (
                        <SkillChip
                          key={index}
                          label={skill} // skill is already translated
                          colorindex={(colorIndex + index) % 3}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box 
                    sx={{ 
                      animation: `${fadeIn} 0.5s ease forwards 0.3s`,
                      opacity: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                      }}
                    >
                      {t('teamDialog.background', 'Background')}
                    </Typography>
                    <InfoItem>
                      <WorkIcon sx={{ color: [colors.primary, colors.secondary, colors.tertiary][colorIndex % 3] }} />
                      <Typography variant="body2">{experience}</Typography> 
                    </InfoItem>
                    <InfoItem>
                      <SchoolIcon sx={{ color: [colors.primary, colors.secondary, colors.tertiary][(colorIndex + 1) % 3] }} />
                      <Typography variant="body2">{education}</Typography>
                    </InfoItem>
                    <InfoItem>
                      <LocationOnIcon sx={{ color: [colors.primary, colors.secondary, colors.tertiary][(colorIndex + 2) % 3] }} />
                      <Typography variant="body2">{location}</Typography>
                    </InfoItem>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default TeamMemberDialog;
